import type { JSX } from 'react';
import type { ClientResource, UpdateUseCaseBody } from '@voxler/contracts';
import { useCaseCategorySchema } from '@voxler/contracts';
import { useCaseCategory } from '@voxler/domain';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Ellipsis, Plus } from 'lucide-react';
import { useState } from 'react';

import { PageChrome } from '../components/page-chrome';
import { useToast } from '../components/toast';
import { CreateUseCaseDialog } from '../components/use-cases/create-use-case-dialog';
import { DuplicateUseCaseDialog } from '../components/use-cases/duplicate-use-case-dialog';
import { UseCaseStatusBadge } from '../components/use-cases/shared';
import { UseCaseSheet } from '../components/use-cases/use-case-sheet';
import { internalFetch, internalSend } from '../lib/api';
import {
  EXECUTION_MODE_LABELS,
  KIND_LABELS,
  type Model,
  smsSettingsOf,
  type UseCase,
  type UseCaseCategory,
} from '../lib/use-cases';
import { Button } from '@/components/ui/button';
import { Card, CardBar } from '@/components/ui/card';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { LoadingState } from '@/components/ui/empty';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { NativeSelect } from '@/components/ui/native-select';
import { PageHead } from '@/components/ui/page-header';
import { Pager, usePagination } from '@/components/ui/pager';
import {
  Cell2,
  rowLinkClass,
  Table,
  TableBody,
  TableCell,
  TableEmptyRow,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { TableTabs, type TableTabItem } from '@/components/ui/tabs';
import { Tag } from '@/components/ui/tag';
import { cn } from '@/lib/utils';

/** Archive filter of the list: archived = `active === false` (D-UC.2). */
type ArchiveFilter = 'active' | 'archived' | 'all';
/** Category filter of the list (lot 17): the category derives from the kind. */
type CategoryFilter = UseCaseCategory | 'all';

const CATEGORY_FILTER_LABELS: Record<CategoryFilter, string> = {
  all: 'Tous',
  ai: 'IA',
  communication: 'Communication',
};

const COLUMNS = 7;

export function UseCasesPage(): JSX.Element {
  const queryClient = useQueryClient();
  const { notify } = useToast();

  const [createOpen, setCreateOpen] = useState(false);
  const [selectedUseCaseId, setSelectedUseCaseId] = useState<string | null>(null);
  const [pendingDelete, setPendingDelete] = useState<UseCase | null>(null);
  const [duplicating, setDuplicating] = useState<UseCase | null>(null);
  // List filters. Default: every client and category, non-archived use cases only.
  const [clientFilter, setClientFilter] = useState('');
  const [categoryFilter, setCategoryFilter] = useState<CategoryFilter>('all');
  const [archiveFilter, setArchiveFilter] = useState<ArchiveFilter>('active');

  const useCases = useQuery({
    queryKey: ['use-cases'],
    queryFn: () => internalFetch<{ useCases: UseCase[] }>('/internal/use-cases'),
  });
  const models = useQuery({
    queryKey: ['models'],
    queryFn: () => internalFetch<{ models: Model[] }>('/internal/models'),
  });
  const clients = useQuery({
    queryKey: ['clients'],
    queryFn: () => internalFetch<{ clients: ClientResource[] }>('/internal/clients'),
  });

  const modelName = (modelId: string): string =>
    models.data?.models.find((model) => model.id === modelId)?.displayName ?? modelId;
  const clientName = (clientId: string | null): string => {
    if (clientId === null) {
      return '—';
    }
    return clients.data?.clients.find((client) => client.id === clientId)?.name ?? clientId;
  };

  const setActive = useMutation({
    mutationFn: (input: { id: string; active: boolean }) => {
      const body: UpdateUseCaseBody = { active: input.active };
      return internalSend<UseCase>('PATCH', `/internal/use-cases/${input.id}`, body);
    },
    onSuccess: (_result, input) => {
      setPendingDelete(null);
      void queryClient.invalidateQueries({ queryKey: ['use-cases'] });
      notify(input.active ? 'Cas d’usage réactivé' : 'Cas d’usage supprimé');
    },
    onError: () => {
      notify('Échec de la mise à jour du cas d’usage');
    },
  });

  const allUseCases = useCases.data?.useCases ?? [];
  // Client and archive filters first: the category tabs count what they would show.
  const scoped = allUseCases
    .filter((row) => clientFilter.length === 0 || row.ownerClientId === clientFilter)
    .filter((row) => {
      switch (archiveFilter) {
        case 'active':
          return row.active;
        case 'archived':
          return !row.active;
        case 'all':
          return true;
      }
    });
  const countOf = (category: CategoryFilter): number =>
    category === 'all'
      ? scoped.length
      : scoped.filter((row) => useCaseCategory(row.kind) === category).length;
  // Most recent first: ids are TypeIDs over UUIDv7, so they sort by creation time.
  const rows = scoped
    .filter((row) => categoryFilter === 'all' || useCaseCategory(row.kind) === categoryFilter)
    .sort((a, b) => (a.id < b.id ? 1 : a.id > b.id ? -1 : 0));
  const pagination = usePagination(rows, 25);
  const hasFilter =
    clientFilter.length > 0 || categoryFilter !== 'all' || archiveFilter !== 'active';
  const selectedUseCase =
    selectedUseCaseId === null
      ? null
      : (allUseCases.find((row) => row.id === selectedUseCaseId) ?? null);

  const categoryFilters: readonly CategoryFilter[] = ['all', ...useCaseCategorySchema.options];
  const categoryTabs: TableTabItem<CategoryFilter>[] = categoryFilters.map((category) => ({
    value: category,
    label: CATEGORY_FILTER_LABELS[category],
    count: useCases.data === undefined ? undefined : countOf(category),
  }));

  return (
    <>
      <PageChrome
        crumbs={[{ label: 'Cas d’usage' }]}
        actions={
          <Button
            type="button"
            onClick={() => {
              setCreateOpen(true);
            }}
          >
            <Plus aria-hidden="true" />
            Créer un cas d’usage
          </Button>
        }
      />
      <PageHead
        title="Cas d’usage"
        subtitle="Configuration, règles de prix et activation par client. Cliquer sur une ligne pour ouvrir la fiche."
      />

      <Card>
        <CardBar
          start={
            <TableTabs
              aria-label="Filtrer par catégorie"
              value={categoryFilter}
              onValueChange={(next) => {
                setCategoryFilter(next);
                pagination.setPage(1);
              }}
              items={categoryTabs}
            />
          }
          end={
            <>
              <NativeSelect
                aria-label="Filtrer par client"
                controlSize="sm"
                wrapperClassName="w-52"
                value={clientFilter}
                onChange={(event) => {
                  setClientFilter(event.target.value);
                  pagination.setPage(1);
                }}
              >
                <option value="">Tous les clients</option>
                {clients.data?.clients.map((client) => (
                  <option key={client.id} value={client.id}>
                    {client.name}
                  </option>
                ))}
              </NativeSelect>
              <NativeSelect
                aria-label="Filtrer par archivage"
                controlSize="sm"
                wrapperClassName="w-40"
                value={archiveFilter}
                onChange={(event) => {
                  switch (event.target.value) {
                    case 'active':
                    case 'archived':
                    case 'all':
                      setArchiveFilter(event.target.value);
                      pagination.setPage(1);
                      break;
                    default:
                      break;
                  }
                }}
              >
                <option value="active">Non archivés</option>
                <option value="archived">Archivés</option>
                <option value="all">Tous</option>
              </NativeSelect>
            </>
          }
        />
        {useCases.isLoading ? (
          <LoadingState />
        ) : (
          <Table className="min-w-[900px]">
            <TableHeader>
              <TableRow>
                <TableHead kind="name">Clé</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Client propriétaire</TableHead>
                <TableHead>Modèle ou service</TableHead>
                <TableHead>Mode</TableHead>
                <TableHead>Statut</TableHead>
                <TableHead kind="act">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {pagination.pageItems.map((useCase) => {
                const sms = smsSettingsOf(useCase);
                return (
                  <TableRow
                    key={useCase.id}
                    selected={useCase.id === selectedUseCaseId}
                    onClick={() => {
                      setSelectedUseCaseId(useCase.id);
                    }}
                  >
                    <TableCell kind="name">
                      <Cell2
                        primary={
                          <button
                            type="button"
                            className={cn(rowLinkClass, 'bg-transparent p-0 font-mono')}
                          >
                            {useCase.key}
                          </button>
                        }
                        secondary={sms !== null ? `Expéditeur : ${sms.sender}` : undefined}
                      />
                    </TableCell>
                    <TableCell>
                      <Tag>{KIND_LABELS[useCase.kind]}</Tag>
                    </TableCell>
                    <TableCell>{clientName(useCase.ownerClientId)}</TableCell>
                    <TableCell>{modelName(useCase.modelId)}</TableCell>
                    <TableCell>{EXECUTION_MODE_LABELS[useCase.executionMode]}</TableCell>
                    <TableCell>
                      <UseCaseStatusBadge useCase={useCase} />
                    </TableCell>
                    <TableCell
                      kind="act"
                      onClick={(event) => {
                        event.stopPropagation();
                      }}
                    >
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon-sm"
                            aria-label={`Actions pour ${useCase.key}`}
                          >
                            <Ellipsis aria-hidden="true" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onSelect={() => {
                              setDuplicating(useCase);
                            }}
                          >
                            Dupliquer
                          </DropdownMenuItem>
                          {useCase.active ? (
                            <DropdownMenuItem
                              variant="destructive"
                              onSelect={() => {
                                setPendingDelete(useCase);
                              }}
                            >
                              Supprimer
                            </DropdownMenuItem>
                          ) : (
                            <DropdownMenuItem
                              disabled={setActive.isPending}
                              onSelect={() => {
                                setActive.mutate({ id: useCase.id, active: true });
                              }}
                            >
                              Réactiver
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </TableCell>
                  </TableRow>
                );
              })}
              {rows.length === 0 ? (
                <TableEmptyRow colSpan={COLUMNS}>
                  {hasFilter
                    ? 'Aucun cas d’usage ne correspond à ces filtres.'
                    : 'Aucun cas d’usage. Créez-en un pour commencer.'}
                </TableEmptyRow>
              ) : null}
            </TableBody>
          </Table>
        )}
        <Pager itemLabel="cas d’usage" {...pagination.pagerProps} pageSizeOptions={[10, 25, 50]} />
      </Card>

      <UseCaseSheet
        useCase={selectedUseCase}
        clients={clients.data?.clients ?? []}
        clientName={clientName}
        modelName={modelName}
        onClose={() => {
          setSelectedUseCaseId(null);
        }}
        onDuplicate={setDuplicating}
        onDelete={setPendingDelete}
        onReactivate={(useCase) => {
          setActive.mutate({ id: useCase.id, active: true });
        }}
        reactivating={setActive.isPending}
      />

      {createOpen ? (
        <CreateUseCaseDialog
          clients={clients.data?.clients ?? []}
          models={models.data?.models ?? []}
          onClose={() => {
            setCreateOpen(false);
          }}
          onCreated={(useCaseId) => {
            setCreateOpen(false);
            // Open its sheet right away (pricing rules, SMS settings).
            setSelectedUseCaseId(useCaseId);
          }}
        />
      ) : null}

      {duplicating !== null ? (
        <DuplicateUseCaseDialog
          source={duplicating}
          clients={clients.data?.clients ?? []}
          onClose={() => {
            setDuplicating(null);
          }}
          onDuplicated={(useCaseId) => {
            setDuplicating(null);
            // Open the copy's sheet: its price starts as the source default price.
            setSelectedUseCaseId(useCaseId);
          }}
        />
      ) : null}

      <ConfirmDialog
        open={pendingDelete !== null}
        onClose={() => {
          setPendingDelete(null);
        }}
        onConfirm={() => {
          if (pendingDelete !== null) {
            setActive.mutate({ id: pendingDelete.id, active: false });
          }
        }}
        title={
          pendingDelete !== null
            ? `Supprimer « ${pendingDelete.key} » ?`
            : 'Supprimer le cas d’usage ?'
        }
        subtitle="Le cas d’usage est désactivé, pas effacé."
        confirmLabel="Supprimer"
        variant="destructive"
        busy={setActive.isPending}
        note="Il ne pourra plus être utilisé pour de nouvelles requêtes. Son historique de facturation est conservé et il reste visible, marqué « Inactif ». Vous pourrez le réactiver à tout moment."
      />
    </>
  );
}
